import * as React from 'react';
import { useDispatch, useSelector } from "react-redux";
import EntityList from "../../components/EntityList";
import { EntityItemTab } from "../../components/EntityList/EntityList";
import { EntityType, Entity, ClientEntity } from "../../store/entities/state";
import { loadEntities, entityCreating } from "../../store/entities/actions";
import { getClientsAsync, getClientAsync, getClientsByIdAsync } from "../../ajax/requests";
import {  getEntitiesDataClient, getEntitiesDataItems } from "../../store/entities/selectors";
import { TK } from "../../store/translations/translationKeys";
import { canAccessClientsChat, canEditClients, canEditOwnClients, canSeeAppointments, canSeeClientReports, canSeeClients, canSeeInvoices, getFeatures, getMyProffesionalData, isAdministrator } from '../../store/session/selectors';
import ClientAppointments from './ClientAppointments';
import ClientEditForm from './ClientEditForm';
import ClientReportsTable from './ClientReportsTable';
import ClientPacksTable from './ClientPacksTable';
import { useTranslations } from '../../store/translations/hooks';
import { useCallback, useMemo, useRef } from 'react';
import { getSettings } from '../../store/settings/selectors';
import { useEntitiesPageSizeSetting } from '../../store/settings/hooks';
import { FeatureEnum } from '../../models';
import { ClientAdvancedSearch, ClientActionsMenu } from './ClientAdvancedSearch';
import ClientInvoicesTable from './ClientInvoicesTable';
import { ClientTags } from '../../components/Forms/fields/ClientTags';
import { ClientCommunications } from '../../modules/ClientCommunications';
import ClientTasksTable from './ClientTasksTable';
import ClientOrdersTable from './ClientOrdersTable';
import {
    createClientSearchConfig,
    getClientFileNumberLabel,
} from '../../shared/clientSearch';
import WarningIcon from '@material-ui/icons/Warning';
import { usePackTemplates } from '../../hooks/usePackTemplates';
import ClientProfileSettingsModal from './ClientProfileSettingsModal';
import ImportClientsModal from './ImportClients/ImportClientsModal';
import ClearDuplicatesModal from './ClearDuplicates/ClearDuplicatesModal';
import { push } from 'connected-react-router';
import { paths } from '../../app/constants';

const ClientsPage: React.FC = () => {
    const t = useTranslations()
    const dispatch = useDispatch()

    const features = useSelector(getFeatures);

    const settings = useSelector(getSettings)
    const professional = useSelector(getMyProffesionalData)
    const isAdmin = useSelector(isAdministrator)
    const canEditClient = useSelector(canEditClients)
    const showClient = useSelector(canSeeClients)
    const canEditOwnClient = useSelector(canEditOwnClients)
    const canSeeAppointmentsPermission = useSelector(canSeeAppointments)
    const showAppointments = Boolean(professional) || canSeeAppointmentsPermission
    const showTasks = showAppointments && Boolean(settings.taskManagement?.enabled);
    const showReports = features.includes(FeatureEnum.Reports)
    const { templates: packTemplates } = usePackTemplates();
    const showPacks = features.includes(FeatureEnum.Packs) && !!packTemplates.length
    const showOrders = settings.extras?.extras?.length > 0
    const showInvoices = useSelector(canSeeInvoices)
    const showCommunications = useSelector(canAccessClientsChat)
    const entitiesData = useSelector(getEntitiesDataClient)
    const entitiesDataItems = useSelector(getEntitiesDataItems<ClientEntity>('clients'))
    const useClientFileNumber = settings.general?.useClientFileNumber === true;
    const pageSize = useEntitiesPageSizeSetting();

    const dataTab: EntityItemTab = useMemo(() => ({ 
        id: 'data',
        title: t(TK.data), 
        render: (_, entity, onClose) => (<ClientEditForm data={entity as ClientEntity} onClose={onClose}/>) 
    }), [t])
    const appointmentsTab: EntityItemTab = useMemo(() => ({ 
        id: 'appointments',
        title: t(TK.appointments), 
        render: (_, entity) => (<ClientAppointments data={entity as ClientEntity} />) 
    }), [t])
    const reportsTab: EntityItemTab = useMemo(() => ({
        id: 'reports',
        title: t(TK.reportsAndDocuments), 
        render: (isOpen, entity) => (<ClientReportsTable isOpen={isOpen} client={entity as ClientEntity} professional={professional}/>) 
    }), [t, professional])
    const packsTab: EntityItemTab = useMemo(() => ({
        id: 'packs',
        title: `${t(TK.packs)}/${t(TK.subscriptions)}`, 
        render: (isOpen, entity) => (<ClientPacksTable isOpen={isOpen} client={entity as ClientEntity}/>) 
    }), [t])
    const invoicesTab: EntityItemTab = useMemo(() => ({
        id: 'invoices',
        title: t(TK.invoices), 
        render: (isOpen, entity) => (<ClientInvoicesTable isOpen={isOpen} client={entity as ClientEntity}/>) 
    }), [t])
    const communicationTab: EntityItemTab = useMemo(() => ({
        id: 'communication',
        title: t(TK.communications), 
        render: (isOpen, entity) => (<ClientCommunications isOpen={isOpen} client={entity as ClientEntity} />) 
    }), [t])
    const tasksTab: EntityItemTab = useMemo(() => ({
        id: 'tasks',
        title: t(TK.tasks),
        render: (isOpen, entity) => (<ClientTasksTable isOpen={isOpen} client={entity as ClientEntity} />),
    }), [t])
    const ordersTab: EntityItemTab = useMemo(() => ({
        id: 'orders',
        title: t(TK.orders),
        render: (isOpen, entity) => (<ClientOrdersTable isOpen={isOpen} client={entity as ClientEntity} />),
    }), [t])

    const [showProfileSettings, setShowProfileSettings] = React.useState(false);
    const [showImportClients, setShowImportClients] = React.useState(false);
    const [showClearDuplicates, setShowClearDuplicates] = React.useState(false);
    const [advancedOpen, setAdvancedOpen] = React.useState(false);

    const fileNumberLabel = t(TK.clientFileNumberLabel);
    const renderItemHeader = useCallback((entity: Entity) => {
        const client = entity as ClientEntity;
        const clientFileNumberText = useClientFileNumber ? getClientFileNumberLabel(client.clientFileNumber, fileNumberLabel) : undefined;

        return (
            <div style={{display: 'flex', flexDirection: 'column', gap: 'var(--spacer-XXS)'}}>
                <span>
                    {client.name} {client.tags?.length ? <ClientTags values={client.tags ?? []}/> : ''}
                </span>
                {clientFileNumberText && (
                    <span style={{fontSize: '0.85rem', color: 'var(--text-body)'}}>
                        {clientFileNumberText}
                    </span>
                )}
                {client.warningNote && (
                    <span style={{ color: 'var(--danger-color)', fontSize: '0.8em', display: 'flex', alignItems: 'center', gap: '2px' }}>
                        <WarningIcon style={{ fontSize: '0.9em' }} />{client.warningNote}
                    </span>
                )}
            </div>
        );
    }, [useClientFileNumber, fileNumberLabel])

    const clientSearchConfig = useMemo(() => createClientSearchConfig(entitiesDataItems), [entitiesDataItems])
    const itemTabs = useMemo(() => [
        ...(showClient || canEditOwnClient ? [dataTab] : []),
        ...(showAppointments ? [appointmentsTab] : []),
        ...(showReports ? [reportsTab] : []),
        ...(showPacks ? [packsTab] : []),
        ...(showInvoices ? [invoicesTab] : []),
        ...(showCommunications ? [communicationTab] : []),
        ...(showTasks ? [tasksTab] : []),
        ...(showOrders ? [ordersTab] : []),
    ].filter(e => !!e), [
        showClient,
        canEditOwnClient,
        showAppointments,
        showTasks,
        showReports,
        showPacks,
        showInvoices,
        showCommunications,
        showOrders,
        dataTab,
        appointmentsTab,
        tasksTab,
        reportsTab,
        packsTab,
        invoicesTab,
        communicationTab,
        ordersTab,
    ])

    const canCreateEntities = useMemo<EntityType[]>(() => canEditClient || canEditOwnClient ? ['clients'] : [], [canEditClient, canEditOwnClient])

    const onLoad = useCallback((keyword?: string, offset?: number, ids?: string[]) => {
        // If no keyword, show all clients
        if (!keyword && offset === 0 && !ids?.length) {
            const loadedClients = Object.values(entitiesDataItems).filter(Boolean) as ClientEntity[];
            if (loadedClients.length > 0) {
                const page = loadedClients.slice(0, pageSize);
                dispatch(loadEntities<ClientEntity>('clients', () => Promise.resolve(page), 'NewResult'));
                return;
            }
        }

        const fetch = (pageSize: number) =>
            !ids?.length ? getClientsAsync(keyword, pageSize, offset)
            : ids.length === 1 ? getClientAsArrayAsync(ids[0])
            : getClientsByIdAsync(ids)
        dispatch(loadEntities<ClientEntity>('clients', fetch, offset ? 'AppendResult' : 'NewResult'))
    }, [dispatch, entitiesDataItems, pageSize])

    const onCreateEntity = useCallback((entityType: EntityType) => dispatch(entityCreating(entityType)), [dispatch])
    const onSearchOptionSelected = useCallback((option: string) => {
        const client = clientSearchConfig.getClientByOption(option);
        if (!client?.id) {
            return;
        }

        onLoad(undefined, 0, [client.id]);
        dispatch(push({pathname: paths.clients(), search: `?id=${client.id}`}));
    }, [clientSearchConfig, dispatch, onLoad])

    return (<>
        <EntityList
            id='clients'
            entityType='clients'
            title={t(TK.clients)}
            entitiesData={entitiesData}
            searchOptions={clientSearchConfig.options}
            searchOptionLabelResolver={clientSearchConfig.optionLabelResolver}
            searchTextResolver={clientSearchConfig.searchTextResolver}
            searchMatcher={clientSearchConfig.searchMatcher}
            onSearchOptionSelected={onSearchOptionSelected}
            advancedOptions={
                <ClientAdvancedSearch
                    isAdmin={isAdmin}
                    onImportClientsClick={() => setShowImportClients(true)}
                    onClientProfileSettingsClick={() => setShowProfileSettings(true)}
                />
            }
            advancedTitle={t(TK.advancedSearch)}
            advancedIsOpen={advancedOpen}
            onAdvancedIsOpenChange={setAdvancedOpen}
            headerActions={
                <ClientActionsMenu
                    isAdvancedOpen={advancedOpen}
                    onToggleAdvanced={() => setAdvancedOpen(o => !o)}
                    isAdmin={isAdmin}
                    onImportClientsClick={() => setShowImportClients(true)}
                    onClientProfileSettingsClick={() => setShowProfileSettings(true)}
                    onClearDuplicatesClick={() => setShowClearDuplicates(true)}
                />
            }
            renderItemHeader={renderItemHeader}
            itemTabs={itemTabs}
            canCreateEntities={canCreateEntities}
            onLoad={onLoad}
            onCreateEntity={onCreateEntity}
            preloadHistory
        />
        {isAdmin && (<>
            <ImportClientsModal
                isOpen={showImportClients}
                onClose={() => setShowImportClients(false)}
            />
            <ClientProfileSettingsModal
                isOpen={showProfileSettings}
                onClose={() => setShowProfileSettings(false)}
            />
            <ClearDuplicatesModal
                isOpen={showClearDuplicates}
                onClose={() => setShowClearDuplicates(false)}
            />
        </>)}
    </>)
}

const getClientAsArrayAsync = async (id: string) => {
    return await getClientAsync(id).then(r => r ? [r] : []);
}

export default ClientsPage
